/**
 * Firebase, loaded on demand.
 *
 * The Firestore + Auth SDKs are roughly 200KB gzipped — more than the rest of
 * the landing page put together. A visitor arriving from an ad should see the
 * hero and be able to start booking before any of that downloads, so nothing
 * here is imported statically. The catalogue renders from the local cache
 * first, and Firestore reconciles it a moment later.
 *
 * `loadFirebase()` memoises the import, so the SDK is fetched and initialised
 * exactly once no matter how many callers ask for it.
 */

/**
 * Web configuration. These values are publishable — they ship in the bundle by
 * design and identify the project, they do not authorise anything. All access
 * control lives in `firestore.rules`.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyCGXD9prvQ62EWdPWRUO_6Ce9bpKmjTOAc',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'eduspire-731ed.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'eduspire-731ed',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'eduspire-731ed.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '948636955760',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:948636955760:web:542840b6ef8b6821a6f0b5',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-V2M12VPKEB',
};

let firebasePromise = null;

/**
 * @returns {Promise<{
 *   app: object, auth: object, db: object,
 *   signInWithEmailAndPassword: Function, signOut: Function, onAuthStateChanged: Function,
 *   collection: Function, doc: Function, getDoc: Function, setDoc: Function,
 *   updateDoc: Function, deleteDoc: Function, onSnapshot: Function,
 * }>}
 */
export function loadFirebase() {
  if (firebasePromise) return firebasePromise;

  firebasePromise = (async () => {
    const [appModule, authModule, firestoreModule, storageModule] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
      import('firebase/firestore'),
      import('firebase/storage'),
    ]);

    const { initializeApp, getApps, getApp } = appModule;
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

    return {
      app,
      auth: authModule.getAuth(app),
      db: firestoreModule.getFirestore(app),
      storage: storageModule.getStorage(app),

      // Storage — testimonial video uploads & storage folder listing.
      storageRef: storageModule.ref,
      uploadBytesResumable: storageModule.uploadBytesResumable,
      getDownloadURL: storageModule.getDownloadURL,
      deleteObject: storageModule.deleteObject,
      listAll: storageModule.listAll,
      getMetadata: storageModule.getMetadata,

      // Auth — the admin console signs in with a code emailed by our API. That
      // code proves identity to *us*; exchanging the custom token the API
      // returns is what gives firestore.rules a real `request.auth` to
      // authorise. Without this exchange every admin write is rejected.
      signInWithCustomToken: authModule.signInWithCustomToken,
      signInWithEmailAndPassword: authModule.signInWithEmailAndPassword,
      signOut: authModule.signOut,
      onAuthStateChanged: authModule.onAuthStateChanged,

      // Firestore
      collection: firestoreModule.collection,
      doc: firestoreModule.doc,
      getDoc: firestoreModule.getDoc,
      getDocs: firestoreModule.getDocs,
      setDoc: firestoreModule.setDoc,
      addDoc: firestoreModule.addDoc,
      updateDoc: firestoreModule.updateDoc,
      deleteDoc: firestoreModule.deleteDoc,
      onSnapshot: firestoreModule.onSnapshot,
      query: firestoreModule.query,
      orderBy: firestoreModule.orderBy,
      limit: firestoreModule.limit,
    };
  })();

  return firebasePromise;
}
